import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { CountryCode } from 'libphonenumber-js';
import { BehaviorSubject } from 'rxjs';
import { Logger } from '../../../../infrastructure/logging/logger';
import { getColor, ColorProp } from '../../../../layout/Colors';
import { OrganizationRow, getPendingOrganizationRowFromOrganization } from '../../models/Organization';
import { OrganizationService } from '../../services/organizations.service';
import { DataTableColumn } from '../../../../shared/components/data-table/DataTableColumn.model';
import { PillComponent } from '../../../../shared/components/pill/pill.component';
import { UserContextProvider } from '../../../../infrastructure/context/user-context-provider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-new-pending-org-table',
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatTabsModule,
    MatTableModule,
    MatFormFieldModule,
    MatSortModule,
    MatPaginatorModule,
    MatInputModule,
    FormsModule,
    MatIconModule,
    PillComponent,
    MatProgressSpinnerModule
  ],
  templateUrl: './new-pending-org-table.component.html',
  styleUrl: './new-pending-org-table.component.scss'
})
export class NewPendingOrgTableComponent implements OnInit, AfterViewInit {

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort: MatSort = new MatSort;

  displayedColumns: DataTableColumn[] = [
    { column: 'id', display: 'ID' },
    { column: 'name', display: 'Name' },
    { column: 'contactName', display: 'Contact Name' },
    { column: 'email', display: 'Email' },
    { column: 'phoneNumber', display: 'Phone' },
    { column: 'pillStatus', display: 'Status' }
  ]

  loading = new BehaviorSubject<boolean>(false);
  displayedColumnNames: string[] = [];
  searchKeyword: string = '';
  tableLength = 0;
  pageSize = 10;
  pageNumber: number = 1;

  pendingOrganizationsDataSource = new MatTableDataSource<OrganizationRow>();
  mappedPendingOrganizations: OrganizationRow[] = [];
  pendingOrganizationsTableLength = 0;
  pendingOrganizationsPageSize = 10;

  initialKeyword = new BehaviorSubject<string>('');
  initialSort = new BehaviorSubject<Sort | undefined>(undefined);
  initialPageSize = new BehaviorSubject<number>(10);
  initialPageNumber = new BehaviorSubject<number>(1);

  constructor(
    private organizationService: OrganizationService,
    private logger: Logger,
    private userContextProvider: UserContextProvider,
    private matSnackbar: MatSnackBar,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.initTableColumns();
    this.loadPendingOrganizations();

    const storedItemsPerPage = localStorage.getItem('pendingOrganizationItemsPerPage');
    if (storedItemsPerPage) {
      this.initialPageSize.next(parseInt(storedItemsPerPage, 10));
    } else {
      this.initialPageSize.next(10);
    }
  }

  ngAfterViewInit() {
    this.pendingOrganizationsDataSource.paginator = this.paginator;
    this.pendingOrganizationsDataSource.sort = this.sort;

    this.initialPageSize?.subscribe(pageSize => {
      this.pageSize = pageSize;
      this.paginator.pageSize = pageSize;
      this.cdr.detectChanges();
    });

    this.initialPageNumber?.subscribe(pageNumber => {
      if (pageNumber != 1) {
        this.pageNumber = pageNumber;
        this.paginator.pageIndex = pageNumber;
      } else {
        this.pageNumber = 1;
      }
      this.cdr.detectChanges();
    });

    this.initialKeyword?.subscribe(keyword => {
      this.searchKeyword = keyword;
      this.cdr.detectChanges();
    });

    this.initialSort?.subscribe(sort => {
      if (!sort) {
        return;
      }

      this.setSort(sort);
      this.cdr.detectChanges();
    });
  }

  setSort(sort: Sort) {
    this.sort.sort({ id: sort.active, start: sort.direction, disableClear: false });
  }

  initTableColumns(): void {
    if (this.displayedColumns === null) {
      this.displayedColumns = [];
    }
    this.displayedColumnNames = this.displayedColumns.map(c => c.column);
  }

  async loadPendingOrganizations() {
    try {
      this.loading.next(true);
      this.pendingOrganizationsDataSource.data = [];

      const organizations = await this.organizationService.getOrganizations('Pending');
      this.mappedPendingOrganizations = organizations
        .filter(org => org.status === "Pending")
        .map((org: any) => getPendingOrganizationRowFromOrganization(org, this.userContextProvider.countryCode as CountryCode));
      this.updatePendingTableDataSource(this.mappedPendingOrganizations);
    } catch (e) {
      this.logger.handleError(e, 'An error occurred while retrieving Pending Organizations response', 'Error while retrieving Pending Organizations response');
    } finally {
      this.loading.next(false);
    }
  }

  updatePendingTableDataSource(pendingOrganizations: OrganizationRow[]) {
    this.pendingOrganizationsDataSource.data = pendingOrganizations;
    this.pendingOrganizationsTableLength = pendingOrganizations.length;
  }

  getOrganizationPillStatusColor(status: string): string {
    switch(status) {
      case "Active":
        return getColor(ColorProp.PropertyStatusActive);
      case "Inactive":
        return '#d3d3d3';
      case "Pending":
        return getColor(ColorProp.ReservationStatusClaimFiled);
      case "New":
        return getColor(ColorProp.GuestProcessing);
      default:
        return '#d3d3d3';
    }
  }

  applySearch(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.pendingOrganizationsDataSource.filter = filterValue.trim().toLowerCase();

    if (this.pendingOrganizationsDataSource.paginator) {
      this.pendingOrganizationsDataSource.paginator.firstPage();
    }
  }

  clearSearch() {
    this.searchKeyword = '';
    this.pendingOrganizationsDataSource = new MatTableDataSource(this.mappedPendingOrganizations);
    this.ngAfterViewInit();
  }

  onGoToPageClick() {
    const max = Math.trunc(Math.ceil(this.paginator.length / this.paginator.pageSize));
    if (this.pageNumber < 1) {
      this.resetPaginator(1, true);
      this.displayInvalidPaginatorPageIndexError();
    } else if (this.pageNumber > max) {
      this.resetPaginator(max, false);
      this.displayInvalidPaginatorPageIndexError();
    } else {
      this.paginator.pageIndex = this.pageNumber - 1;
      this.paginator.page.next({
        pageIndex: this.pageNumber - 1,
        pageSize: this.paginator.pageSize,
        length: this.paginator.length
      });
    }
  }

  resetPaginator(pageNumber: number, goToFirstPage: boolean) {
    this.pageNumber = pageNumber;

    if (this.paginator) {
      if (goToFirstPage) {
        this.paginator.firstPage();
      } else {
        this.paginator.lastPage();
      }
    }
  }

  displayInvalidPaginatorPageIndexError() {
    this.matSnackbar.open('Invalid page number', 'OK', { duration: 3000 });
  }

  onRowClick(row: any): void {
    this.router.navigate([`/organizations/detail/${row['id']}`]);
  }

  onPageEvent(event: PageEvent): void {
    this.pageNumber = event.pageIndex + 1;
    localStorage.setItem('pendingOrganizationItemsPerPage', event.pageSize.toString());
  }
}